/*
 * Copyright 2026 Sony Corporation
 */

import { Box, Typography } from '@mui/material';

import type { AgentOp } from '../../services/agent';

/**
 * What a turn would do, in the op vocabulary's own terms.
 *
 * Ops, not prose: the model's description of its work is not evidence, and the
 * whole point of the vocabulary is that the change can be read before it is
 * accepted. Repeats are counted rather than listed, because twenty-four lines
 * saying *Add device* is a wall, not a review.
 */

const count = (total: number, noun: string): string => `${total} ${noun}${total === 1 ? '' : 's'}`;

const describe = (op: AgentOp): string => {
  switch (op.op) {
    case 'add_primitive':
      return `Add ${op.type}`;
    case 'add_light':
      return `Add ${op.type} light`;
    case 'add_asset':
      return `Add ${op.name ?? op.slug}`;
    case 'set_environment':
      return op.slug === null ? 'Remove the environment' : `Environment → ${op.slug}`;
    case 'set_material':
      return op.slug === null ? `Clear the texture on ${count(op.node_ids.length, 'object')}` : `Texture → ${op.slug}`;
    case 'set_transform':
      return 'Move an object';
    case 'duplicate':
      return `Duplicate ${count(op.node_ids.length, 'object')}`;
    case 'delete':
      return `Delete ${count(op.node_ids.length, 'object')}`;
    case 'add_device':
      return 'Add a device';
    case 'set_pose':
      return 'Move a device';
    case 'look_at':
      return 'Aim a device';
    case 'remove_device':
      return 'Remove a device';
    case 'set_optics':
      return 'Change a lens';
  }
};

const summarize = (ops: readonly AgentOp[]): { text: string; times: number }[] => {
  const rows: { text: string; times: number }[] = [];
  for (const op of ops) {
    const text = describe(op);
    const last = rows.at(-1);
    if (last?.text === text) {
      last.times += 1;
    } else {
      rows.push({ text, times: 1 });
    }
  }
  return rows;
};

export interface OpsDiffProps {
  ops: readonly AgentOp[];
}

export const OpsDiff = ({ ops }: OpsDiffProps) => (
  <Box className="mt-1 rounded border px-2 py-1" sx={{ borderColor: 'divider' }}>
    <Typography variant="caption" color="text.secondary" className="block">
      {count(ops.length, 'change')}
    </Typography>
    {summarize(ops).map((row, index) => (
      <Typography key={`${row.text}-${index}`} variant="caption" className="block font-mono">
        {row.text}
        {row.times > 1 && (
          <Typography component="span" variant="caption" color="text.secondary">
            {` ×${row.times}`}
          </Typography>
        )}
      </Typography>
    ))}
  </Box>
);
